/**
 * Wire protocol for walk-in polls: the `pollState` room custom property,
 * Photon event 58 command payloads, and the pure zone/tally math. Pure and
 * window-free (like breakoutProtocol.ts) so it unit-tests in node.
 *
 * Sync model (see docs/walk-in-polls.md):
 * - Only `runPolls` actors write state: property FIRST, then event 58 (the
 *   worldSession convention). Receivers re-read the property on every
 *   command, so a missed event self-heals at the next hydration point.
 * - Votes are just positions. Every client derives zone counts locally from
 *   the existing 10Hz position stream (event 1) - nothing per-vote is ever
 *   broadcast. Zone anchors come from per-level config
 *   (src/config/pollZones.config.js), NOT from this wire state, so the
 *   property stays small and a client in another level degrades to a banner.
 * - On Close, the CLOSING client snapshots the tallies from its own local
 *   view into `closedTallies`/`totalVoters`, so every client renders the same
 *   frozen results even after voters walk away.
 *
 * Decoders validate everything: the property and events are peer-writable and
 * never trusted raw. Sender identity always comes from the server-stamped
 * onEvent actorNr, and receivers additionally gate event 58 on the sender's
 * `runPolls` capability.
 */

import { CONFIG } from '../config/client.config.js';

/** Host -> room: poll command (open / close / clear). */
export const POLL_EVENT_CODE = CONFIG.POLL.EVENT_CODE;
/** Room custom property holding the full poll state (JSON string). */
export const POLL_STATE_ROOM_PROP = CONFIG.POLL.ROOM_PROP;
export const MAX_QUESTION_LENGTH = CONFIG.POLL.MAX_QUESTION_LENGTH;
export const MAX_OPTION_LABEL_LENGTH = CONFIG.POLL.MAX_OPTION_LABEL_LENGTH;
export const MIN_OPTIONS = CONFIG.POLL.MIN_OPTIONS;
export const MAX_OPTIONS = CONFIG.POLL.MAX_OPTIONS;
/** Sticky-zone hysteresis: leave only past radius * (1 + EXIT_MARGIN). */
export const EXIT_MARGIN = CONFIG.POLL.EXIT_MARGIN;

const ACTIONS = ['open', 'close', 'clear'] as const;
/** pollId charset - short, URL/key-safe, lowercase. */
const ID_RE = /^[a-z0-9-]{1,32}$/;
/** Option ids are always a prefix of this, in order (zone letters). */
export const OPTION_IDS = ['a', 'b', 'c', 'd'] as const;
const MAX_STATE_BYTES = 2048;
const MAX_LEVEL_ID_LENGTH = 120;
const MAX_TALLY = 10000;

export type PollAction = (typeof ACTIONS)[number];
export type PollOptionId = (typeof OPTION_IDS)[number];
export type PollStatus = 'open' | 'closed';

export interface PollOption {
  /** zone letter ('a'..'d'), fixed by index */
  id: string;
  label: string;
}

export interface PollState {
  active: true;
  /** fresh per "Open poll"; idempotency key */
  pollId: string;
  /** level whose anchors host the zones */
  levelId: string;
  question: string;
  options: PollOption[];
  status: PollStatus;
  /** optionId -> count, written by the closer */
  closedTallies: Record<string, number> | null;
  totalVoters: number | null;
  /** epoch ms */
  closedAt: number | null;
  /** epoch ms */
  openedAt: number;
  /** actorNr, informational */
  openedBy: number;
  /** monotonic revision; readers ignore regressions */
  rev: number;
}

export interface InactivePollState {
  active: false;
}

export type AnyPollState = PollState | InactivePollState;

export interface PollZone {
  /** option id ('a'..'d') */
  id: string;
  x: number;
  z: number;
  radius: number;
}

export interface PollCommand {
  action: PollAction;
  pollId: string;
}

function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

function isValidId(id: unknown): id is string {
  return typeof id === 'string' && ID_RE.test(id);
}

/** Mint a fresh poll id (8 base36 chars). */
export function mintPollId(rng: () => number = Math.random): string {
  let out = '';
  while (out.length < 8) out += rng().toString(36).slice(2);
  return out.slice(0, 8);
}

/** Normalize a proposed question; null when unusable. */
export function normalizeQuestion(question: unknown): string | null {
  if (typeof question !== 'string') return null;
  const trimmed = question.trim().slice(0, MAX_QUESTION_LENGTH);
  return trimmed.length ? trimmed : null;
}

/** Normalize a proposed option label; null when unusable. */
export function normalizeOptionLabel(label: unknown): string | null {
  if (typeof label !== 'string') return null;
  const trimmed = label.trim().slice(0, MAX_OPTION_LABEL_LENGTH);
  return trimmed.length ? trimmed : null;
}

/**
 * Encode state for the room property. Callers pass a fully-formed state
 * (build it via decode-mutate-encode or the writer in multiplayer.js).
 */
export function encodePollState(state: AnyPollState): string {
  return JSON.stringify({ v: 1, ...state });
}

/**
 * Decode + validate the `pollState` room property.
 * Returns:
 * - a validated {@link PollState} when active,
 * - `{active: false}` for a well-formed inactive state,
 * - `null` for anything malformed (peer-writable - fail closed to "no poll",
 *   never to a partially-trusted state).
 */
export function decodePollState(raw: unknown): AnyPollState | null {
  if (raw == null) return null;
  if (typeof raw !== 'string' || raw.length > MAX_STATE_BYTES) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const record = parsed as Record<string, unknown>;
  if (record.v !== 1) return null;
  if (record.active !== true) return { active: false };

  const {
    pollId,
    levelId,
    question,
    options,
    status,
    closedTallies,
    totalVoters,
    closedAt,
    openedAt,
    openedBy,
    rev,
  } = record;
  if (!isValidId(pollId)) return null;
  if (typeof levelId !== 'string' || !levelId.length || levelId.length > MAX_LEVEL_ID_LENGTH)
    return null;
  const cleanQuestion = normalizeQuestion(question);
  if (!cleanQuestion || cleanQuestion !== question) return null;
  if (!Array.isArray(options) || options.length < MIN_OPTIONS || options.length > MAX_OPTIONS)
    return null;
  const cleanOptions: PollOption[] = [];
  for (let i = 0; i < options.length; i++) {
    const option = options[i];
    if (!option || typeof option !== 'object') return null;
    // Option ids are positional zone letters - anything else is malformed.
    if (option.id !== OPTION_IDS[i]) return null;
    const label = normalizeOptionLabel(option.label);
    if (!label || label !== option.label) return null;
    cleanOptions.push({ id: option.id, label });
  }
  if (status !== 'open' && status !== 'closed') return null;
  let cleanTallies: Record<string, number> | null = null;
  if (status === 'closed') {
    // A closed poll without its frozen results is malformed - results are the
    // whole point of the closed state persisting until Clear.
    if (!closedTallies || typeof closedTallies !== 'object' || Array.isArray(closedTallies))
      return null;
    cleanTallies = {};
    const optionIds = new Set(cleanOptions.map((o) => o.id));
    for (const [optionId, count] of Object.entries(closedTallies)) {
      if (!optionIds.has(optionId)) return null;
      if (!Number.isInteger(count) || count < 0 || count > MAX_TALLY) return null;
      cleanTallies[optionId] = count;
    }
    for (const optionId of optionIds) {
      if (!(optionId in cleanTallies)) return null;
    }
    if (
      !Number.isInteger(totalVoters) ||
      (totalVoters as number) < 0 ||
      (totalVoters as number) > MAX_TALLY
    )
      return null;
    if (!(isFiniteNumber(closedAt) && closedAt > 0)) return null;
  } else {
    if (closedTallies !== null && closedTallies !== undefined) return null;
    if (totalVoters !== null && totalVoters !== undefined) return null;
    if (closedAt !== null && closedAt !== undefined) return null;
  }
  if (!(isFiniteNumber(openedAt) && openedAt > 0)) return null;
  if (!Number.isInteger(openedBy) || (openedBy as number) < 1) return null;
  if (!Number.isInteger(rev) || (rev as number) < 0) return null;

  return {
    active: true,
    pollId,
    levelId,
    question: cleanQuestion,
    options: cleanOptions,
    status,
    closedTallies: cleanTallies,
    totalVoters: status === 'closed' ? (totalVoters as number) : null,
    closedAt: status === 'closed' ? (closedAt as number) : null,
    openedAt,
    openedBy: openedBy as number,
    rev: rev as number,
  };
}

export function encodePollCommand(
  pollId: string,
  action: PollAction,
): { v: 1; pollId: string; action: string } {
  return { v: 1, pollId, action };
}

/** Decode + validate an event 58 payload. */
export function decodePollCommand(content: unknown): PollCommand | null {
  if (!content || typeof content !== 'object') return null;
  const { v, pollId, action } = content as { v?: unknown; pollId?: unknown; action?: unknown };
  if (v !== 1 || !isValidId(pollId) || !ACTIONS.includes(action as PollAction)) return null;
  return { action: action as PollAction, pollId };
}

/**
 * Which zone is a participant voting in? Sticky: while within
 * radius * (1 + exitMargin) of the PREVIOUS zone they stay there, so pacing a
 * boundary never flickers a vote. Otherwise smallest normalized d^2/r^2 wins
 * (the pickZone rule from dwellMeter.js - exactly one zone per person).
 *
 * @returns zone id, or null when outside every zone
 */
export function resolveZone(
  zones: PollZone[],
  x: number,
  z: number,
  prevZoneId: string | null,
  exitMargin: number = EXIT_MARGIN,
): string | null {
  if (!Array.isArray(zones) || !isFiniteNumber(x) || !isFiniteNumber(z)) return null;
  if (prevZoneId) {
    const prev = zones.find((zone) => zone.id === prevZoneId);
    if (prev) {
      const dx = x - prev.x;
      const dz = z - prev.z;
      const stickyR = prev.radius * (1 + exitMargin);
      if (dx * dx + dz * dz <= stickyR * stickyR) return prev.id;
    }
  }
  let bestId: string | null = null;
  let bestScore = Infinity;
  for (const zone of zones) {
    const rSq = zone.radius * zone.radius;
    if (!(rSq > 0)) continue;
    const dx = x - zone.x;
    const dz = z - zone.z;
    const dSq = dx * dx + dz * dz;
    if (dSq > rSq) continue;
    const score = dSq / rSq;
    if (score < bestScore) {
      bestScore = score;
      bestId = zone.id;
    }
  }
  return bestId;
}

/**
 * Count every participant's zone in one pass. `prevZoneMap` carries each
 * participant's zone from the previous tick (for hysteresis); the returned
 * `zoneByParticipant` is the next tick's prevZoneMap.
 */
export function tallyZones(
  participants: Array<{ id: string; x: number; z: number }>,
  zones: PollZone[],
  prevZoneMap: Record<string, string | null> | null | undefined,
  exitMargin: number = EXIT_MARGIN,
): {
  counts: Record<string, number>;
  total: number;
  zoneByParticipant: Record<string, string | null>;
} {
  const counts: Record<string, number> = {};
  for (const zone of zones) counts[zone.id] = 0;
  const zoneByParticipant: Record<string, string | null> = {};
  let total = 0;
  for (const participant of participants) {
    const zoneId = resolveZone(
      zones,
      participant.x,
      participant.z,
      prevZoneMap?.[participant.id] ?? null,
      exitMargin,
    );
    zoneByParticipant[participant.id] = zoneId;
    if (zoneId != null && zoneId in counts) {
      counts[zoneId] = (counts[zoneId] ?? 0) + 1;
      total++;
    }
  }
  return { counts, total, zoneByParticipant };
}

/**
 * Winning option id(s) from frozen tallies - all tied leaders, or [] when
 * nobody voted.
 */
export function computeWinners(tallies: Record<string, number> | null | undefined): string[] {
  if (!tallies || typeof tallies !== 'object') return [];
  let best = 0;
  for (const count of Object.values(tallies)) {
    if (Number.isInteger(count) && count > best) best = count;
  }
  if (best === 0) return [];
  return Object.keys(tallies).filter((id) => tallies[id] === best);
}
